import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import {
  IntellectDesignTransferResponse,
  IntellectDesignBalanceResponse,
} from '@/types/api/intellect';
import { Loader2, CheckCircle2, XCircle, ArrowDownCircle, ArrowUpCircle, Wallet, Building2, Beaker } from 'lucide-react';

/**
 * Intellect Design Proof-of-Concept Page
 * 
 * Hidden behind ?intellect=on feature toggle.
 * This is a PoC with dummy API calls. Real API integration will replace these when credentials are available.
 */

// Dummy API service - simulates API calls with delays
const dummyApiService = {
  // Simulate API delay
  delay: (ms: number) => new Promise(resolve => setTimeout(resolve, ms)),

  // Mock transfer API call
  async transfer(
    fromAccount: string,
    toAccount: string,
    amount: number,
    currency: string
  ): Promise<IntellectDesignTransferResponse> {
    await this.delay(800); // Simulate network delay
    
    // Simulate 5% failure rate for demo purposes
    if (Math.random() < 0.05) {
      throw new Error('Simulated API error: Network timeout');
    }

    return {
      success: true,
      transactionId: `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      message: `Successfully transferred ${amount} ${currency} from ${fromAccount} to ${toAccount}`,
      data: {
        fromAccount,
        toAccount,
        amount,
        currency,
        timestamp: new Date().toISOString(),
      },
    };
  },

  // Mock balance API call
  async getBalance(accountId: string, currency: string = 'USD'): Promise<IntellectDesignBalanceResponse> {
    await this.delay(500); // Simulate network delay
    
    // Return mock balance based on account ID
    const mockBalances: Record<string, number> = {
      A: 1000.00,
      B: 500.00,
      C: 2500.00, // Central Token Account starts with higher balance
    };

    return {
      success: true,
      accountId,
      balance: mockBalances[accountId] || 0,
      currency,
      message: `Balance retrieved successfully for account ${accountId}`,
      data: {
        accountId,
        balance: mockBalances[accountId] || 0,
        currency,
        lastUpdated: new Date().toISOString(),
      },
    };
  },
};

const IntellectDesignTestPage: React.FC = () => {
  // Sandbox accounts - fixed accounts A, B, C
  const [accountA, setAccountA] = useState('A');
  const [accountB, setAccountB] = useState('B');
  const [accountC, setAccountC] = useState('C'); // Central Token Account

  // Transaction amount
  const [transactionAmount, setTransactionAmount] = useState('');
  const [transactionCurrency, setTransactionCurrency] = useState('USD');

  // Withdrawal state (debit A, credit C)
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawalResponse, setWithdrawalResponse] = useState<IntellectDesignTransferResponse | null>(null);
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null);

  // Deposit state (debit C, credit A)
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositResponse, setDepositResponse] = useState<IntellectDesignTransferResponse | null>(null);
  const [depositError, setDepositError] = useState<string | null>(null);

  // Balance state for all accounts - initialized with dummy data
  const [balances, setBalances] = useState<{
    A: IntellectDesignBalanceResponse | null;
    B: IntellectDesignBalanceResponse | null;
    C: IntellectDesignBalanceResponse | null;
  }>({
    A: { success: true, accountId: 'A', balance: 1000.00, currency: 'USD' },
    B: { success: true, accountId: 'B', balance: 500.00, currency: 'USD' },
    C: { success: true, accountId: 'C', balance: 2500.00, currency: 'USD' },
  });
  
  const [balanceLoading, setBalanceLoading] = useState<{ A: boolean; B: boolean; C: boolean }>({
    A: false,
    B: false,
    C: false,
  });
  const [balanceErrors, setBalanceErrors] = useState<{ A: string | null; B: string | null; C: string | null }>({
    A: null,
    B: null,
    C: null,
  });

  // Load initial balances on mount
  useEffect(() => {
    refreshAllBalances();
  }, []);

  // Update balance locally after successful transaction
  const updateBalanceLocally = (accountKey: 'A' | 'B' | 'C', newBalance: number) => {
    setBalances((prev) => {
      const current = prev[accountKey];
      if (current) {
        return {
          ...prev,
          [accountKey]: {
            ...current,
            balance: newBalance,
          },
        };
      }
      return prev;
    });
  };

  // Handle withdrawal test (debit A, credit C)
  const handleWithdrawal = async () => {
    if (!transactionAmount) {
      setWithdrawalError('Please enter an amount');
      return;
    }

    setWithdrawalLoading(true);
    setWithdrawalError(null);
    setWithdrawalResponse(null);

    try {
      const amount = parseFloat(transactionAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Amount must be a positive number');
      }

      // Check if Account A has sufficient balance
      const currentBalanceA = balances.A?.balance || 0;
      if (currentBalanceA < amount) {
        throw new Error(`Insufficient balance. Account A has ${currentBalanceA} ${transactionCurrency}`);
      }

      // Call dummy API (will be replaced with real API)
      const response = await dummyApiService.transfer(
        accountA,
        accountC,
        amount,
        transactionCurrency
      );

      setWithdrawalResponse(response);

      // Update balances locally: debit A, credit C
      const newBalanceA = currentBalanceA - amount;
      const newBalanceC = (balances.C?.balance || 0) + amount;
      
      updateBalanceLocally('A', newBalanceA);
      updateBalanceLocally('C', newBalanceC);
    } catch (error) {
      const err = error as Error;
      setWithdrawalError(err.message || 'Withdrawal failed');
    } finally {
      setWithdrawalLoading(false);
    }
  };

  // Handle deposit test (debit C, credit A)
  const handleDeposit = async () => {
    if (!transactionAmount) {
      setDepositError('Please enter an amount');
      return;
    }

    setDepositLoading(true);
    setDepositError(null);
    setDepositResponse(null);

    try {
      const amount = parseFloat(transactionAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Amount must be a positive number');
      }

      // Check if Account C has sufficient balance
      const currentBalanceC = balances.C?.balance || 0;
      if (currentBalanceC < amount) {
        throw new Error(`Insufficient balance. Account C has ${currentBalanceC} ${transactionCurrency}`);
      }

      // Call dummy API (will be replaced with real API)
      const response = await dummyApiService.transfer(
        accountC,
        accountA,
        amount,
        transactionCurrency
      );

      setDepositResponse(response);

      // Update balances locally: debit C, credit A
      const newBalanceC = currentBalanceC - amount;
      const newBalanceA = (balances.A?.balance || 0) + amount;
      
      updateBalanceLocally('C', newBalanceC);
      updateBalanceLocally('A', newBalanceA);
    } catch (error) {
      const err = error as Error;
      setDepositError(err.message || 'Deposit failed');
    } finally {
      setDepositLoading(false);
    }
  };

  // Refresh balance for a specific account
  const refreshBalance = async (accountId: string, accountKey: 'A' | 'B' | 'C') => {
    setBalanceLoading((prev) => ({ ...prev, [accountKey]: true }));
    setBalanceErrors((prev) => ({ ...prev, [accountKey]: null }));

    try {
      // Call dummy API (will be replaced with real API)
      const response = await dummyApiService.getBalance(accountId, transactionCurrency);
      setBalances((prev) => ({ ...prev, [accountKey]: response }));
    } catch (error) {
      const err = error as Error;
      setBalanceErrors((prev) => ({
        ...prev,
        [accountKey]: err.message || 'Balance retrieval failed',
      }));
    } finally {
      setBalanceLoading((prev) => ({ ...prev, [accountKey]: false }));
    }
  };

  // Refresh all account balances
  const refreshAllBalances = async () => {
    await Promise.all([
      refreshBalance(accountA, 'A'),
      refreshBalance(accountB, 'B'),
      refreshBalance(accountC, 'C'),
    ]);
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-3xl font-bold">Intellect Design PoC</h1>
          <Beaker className="h-6 w-6 text-orange-500" />
        </div>
        <Alert className="mb-4">
          <Beaker className="h-4 w-4" />
          <AlertDescription>
            <strong>Proof of Concept:</strong> This page uses dummy API calls to demonstrate the withdrawal and deposit flows. 
            Real API integration will be implemented once credentials are available. Use Postman for testing actual Intellect Design APIs.
          </AlertDescription>
        </Alert>
        <p className="text-muted-foreground">
          This page is hidden behind the{' '}
          <code className="bg-muted px-1 py-0.5 rounded">?intellect=on</code> feature toggle.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Account Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Account Configuration
            </CardTitle>
            <CardDescription>
              Configure sandbox account identifiers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="accountA">Account A ID</Label>
              <Input
                id="accountA"
                placeholder="Account A"
                value={accountA}
                onChange={(e) => setAccountA(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountB">Account B ID</Label>
              <Input
                id="accountB"
                placeholder="Account B"
                value={accountB}
                onChange={(e) => setAccountB(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountC">Account C ID (Central Token Account)</Label>
              <Input
                id="accountC"
                placeholder="Account C"
                value={accountC}
                onChange={(e) => setAccountC(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Account C is the Central Token Account used for withdrawals and deposits
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Transaction Testing Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowDownCircle className="h-5 w-5" />
              Transaction Testing
            </CardTitle>
            <CardDescription>
              Test withdrawal and deposit operations (PoC with dummy APIs)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="transactionAmount">Amount</Label>
                <Input
                  id="transactionAmount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={transactionAmount}
                  onChange={(e) => setTransactionAmount(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="transactionCurrency">Currency</Label>
                <Input
                  id="transactionCurrency"
                  placeholder="USD"
                  value={transactionCurrency}
                  onChange={(e) => setTransactionCurrency(e.target.value.toUpperCase())}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-semibold mb-2">Withdrawal</p>
                <p className="text-xs text-muted-foreground">
                  Debits Account A, Credits Account C (Central Token Account)
                </p>
              </div>
              <Button
                onClick={handleWithdrawal}
                disabled={withdrawalLoading}
                className="w-full"
                variant="outline"
              >
                {withdrawalLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing Withdrawal...
                  </>
                ) : (
                  <>
                    <ArrowDownCircle className="h-4 w-4" />
                    Test Withdrawal
                  </>
                )}
              </Button>

              {withdrawalError && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{withdrawalError}</AlertDescription>
                </Alert>
              )}

              {withdrawalResponse && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    <div className="mt-2">
                      <p className="font-semibold text-sm">Withdrawal Successful</p>
                      <p className="text-xs mt-1">{withdrawalResponse.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Transaction ID: {withdrawalResponse.transactionId}
                      </p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-3 pt-4 border-t">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-semibold mb-2">Deposit</p>
                <p className="text-xs text-muted-foreground">
                  Debits Account C (Central Token Account), Credits Account A
                </p>
              </div>
              <Button
                onClick={handleDeposit}
                disabled={depositLoading}
                className="w-full"
                variant="outline"
              >
                {depositLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing Deposit...
                  </>
                ) : (
                  <>
                    <ArrowUpCircle className="h-4 w-4" />
                    Test Deposit
                  </>
                )}
              </Button>

              {depositError && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{depositError}</AlertDescription>
                </Alert>
              )}

              {depositResponse && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    <div className="mt-2">
                      <p className="font-semibold text-sm">Deposit Successful</p>
                      <p className="text-xs mt-1">{depositResponse.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Transaction ID: {depositResponse.transactionId}
                      </p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Balances Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Account Balances
          </CardTitle>
          <CardDescription>
            Real-time balances for all sandbox accounts (updated automatically after transactions)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Account A */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Account A</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refreshBalance(accountA, 'A')}
                  disabled={balanceLoading.A}
                >
                  {balanceLoading.A ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Wallet className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {balanceErrors.A ? (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription className="text-xs">{balanceErrors.A}</AlertDescription>
                </Alert>
              ) : balances.A ? (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">
                    {balances.A.balance !== undefined
                      ? `${balances.A.balance.toFixed(2)} ${balances.A.currency || 'USD'}`
                      : 'N/A'}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Loading...</p>
              )}
            </div>

            {/* Account B */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Account B</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refreshBalance(accountB, 'B')}
                  disabled={balanceLoading.B}
                >
                  {balanceLoading.B ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Wallet className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {balanceErrors.B ? (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription className="text-xs">{balanceErrors.B}</AlertDescription>
                </Alert>
              ) : balances.B ? (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">
                    {balances.B.balance !== undefined
                      ? `${balances.B.balance.toFixed(2)} ${balances.B.currency || 'USD'}`
                      : 'N/A'}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Loading...</p>
              )}
            </div>

            {/* Account C - Central Token Account */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Account C</Label>
                  <p className="text-xs text-muted-foreground">Central Token Account</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refreshBalance(accountC, 'C')}
                  disabled={balanceLoading.C}
                >
                  {balanceLoading.C ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Wallet className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {balanceErrors.C ? (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription className="text-xs">{balanceErrors.C}</AlertDescription>
                </Alert>
              ) : balances.C ? (
                <div className="p-3 bg-muted rounded-lg border-2 border-primary">
                  <p className="text-2xl font-bold">
                    {balances.C.balance !== undefined
                      ? `${balances.C.balance.toFixed(2)} ${balances.C.currency || 'USD'}`
                      : 'N/A'}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Loading...</p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <Button
              onClick={refreshAllBalances}
              disabled={balanceLoading.A || balanceLoading.B || balanceLoading.C}
              variant="outline"
              className="w-full"
            >
              {balanceLoading.A || balanceLoading.B || balanceLoading.C ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Refreshing All Balances...
                </>
              ) : (
                <>
                  <Wallet className="h-4 w-4" />
                  Refresh All Balances
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntellectDesignTestPage;
